import { useEffect, useMemo, useState } from "react";

import { CategoryPicker } from "@/components/admin/category-picker";
import { PageHead } from "@/components/admin/page-head";
import { useConfirm, useToast } from "@/components/ui/feedback";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import {
	useAttributes,
	useCategoriesList,
	useCategoryAttributes,
	useCreateAttribute,
	useDeleteAttribute,
	useSetCategoryAttributes,
	useUpdateAttribute,
} from "@/hooks/use-catalog";
import type { AttributeDto, AttributeType } from "@/lib/catalog/catalog-api";

const TYPE_LABEL: Record<AttributeType, string> = {
	select: "Liste de choix",
	text: "Texte libre",
	number: "Nombre",
	boolean: "Oui / Non",
};

function Modal({
	title,
	onClose,
	children,
}: {
	title: string;
	onClose: () => void;
	children: React.ReactNode;
}) {
	return (
		<div
			onMouseDown={onClose}
			style={{
				position: "fixed",
				inset: 0,
				background: "rgba(0,0,0,0.35)",
				zIndex: 100,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				padding: 16,
			}}
		>
			<div
				onMouseDown={(e) => e.stopPropagation()}
				className="card"
				style={{
					width: "min(560px, 100%)",
					maxHeight: "90vh",
					overflowY: "auto",
				}}
			>
				<div className="card-h">
					<h3>{title}</h3>
				</div>
				<div className="card-b">{children}</div>
			</div>
		</div>
	);
}

function AttributeModal({
	target,
	onClose,
}: {
	target: "new" | AttributeDto;
	onClose: () => void;
}) {
	const toast = useToast();
	const createMut = useCreateAttribute();
	const updateMut = useUpdateAttribute();
	const isNew = target === "new";
	const attr = isNew ? null : target;
	const [label, setLabel] = useState(attr?.label ?? "");
	const [type, setType] = useState<AttributeType>(attr?.type ?? "select");
	const [unit, setUnit] = useState(attr?.unit ?? "");
	const [valuesText, setValuesText] = useState(
		(attr?.values ?? []).map((v) => v.value).join("\n"),
	);
	const [err, setErr] = useState<string | null>(null);

	const save = async () => {
		setErr(null);
		const values = valuesText
			.split("\n")
			.map((s) => s.trim())
			.filter(Boolean);
		const payload = {
			label,
			type,
			unit: unit || null,
			...(type === "select" ? { values } : {}),
		};
		try {
			if (isNew) await createMut.mutateAsync(payload);
			else await updateMut.mutateAsync({ id: attr!.id, payload });
			toast.success(isNew ? "Attribut créé." : "Attribut mis à jour.");
			onClose();
		} catch (e) {
			setErr(extractErrorMessage(e, "Enregistrement impossible"));
		}
	};

	return (
		<Modal
			title={isNew ? "Nouvel attribut" : `Modifier « ${attr!.label} »`}
			onClose={onClose}
		>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr",
					gap: 12,
					marginBottom: 12,
				}}
			>
				<div>
					<label
						style={{
							display: "block",
							fontSize: 12,
							fontWeight: 600,
							marginBottom: 4,
						}}
					>
						Libellé
					</label>
					<input
						className="aform-i"
						autoFocus
						value={label}
						onChange={(e) => setLabel(e.target.value)}
						placeholder="Ex. RAM"
						style={{ width: "100%" }}
					/>
				</div>
				<div>
					<label
						style={{
							display: "block",
							fontSize: 12,
							fontWeight: 600,
							marginBottom: 4,
						}}
					>
						Type
					</label>
					<select
						className="aform-i"
						value={type}
						onChange={(e) => setType(e.target.value as AttributeType)}
						style={{ width: "100%" }}
					>
						{(Object.keys(TYPE_LABEL) as AttributeType[]).map((t) => (
							<option key={t} value={t}>
								{TYPE_LABEL[t]}
							</option>
						))}
					</select>
				</div>
			</div>
			<label
				style={{
					display: "block",
					fontSize: 12,
					fontWeight: 600,
					marginBottom: 4,
				}}
			>
				Unité (optionnel)
			</label>
			<input
				className="aform-i"
				value={unit}
				onChange={(e) => setUnit(e.target.value)}
				placeholder="Ex. Go, cm, W"
				style={{ width: "100%", marginBottom: 12 }}
			/>
			{type === "select" && (
				<>
					<label
						style={{
							display: "block",
							fontSize: 12,
							fontWeight: 600,
							marginBottom: 4,
						}}
					>
						Valeurs autorisées (une par ligne)
					</label>
					<textarea
						className="aform-i"
						rows={5}
						value={valuesText}
						onChange={(e) => setValuesText(e.target.value)}
						placeholder={"4 Go\n8 Go\n16 Go"}
						style={{ width: "100%", marginBottom: 12 }}
					/>
				</>
			)}
			{err && (
				<div
					style={{
						color: "var(--a-danger, #c00)",
						fontSize: 12,
						marginBottom: 8,
					}}
				>
					{err}
				</div>
			)}
			<div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
				<button className="btn btn-ghost" onClick={onClose}>
					Annuler
				</button>
				<button
					className="btn btn-primary"
					onClick={() => void save()}
					disabled={!label.trim() || createMut.isPending || updateMut.isPending}
				>
					Enregistrer
				</button>
			</div>
		</Modal>
	);
}

/** Section : rattachement des attributs à une catégorie. */
function CategoryAssignment({ attributes }: { attributes: AttributeDto[] }) {
	const { data: cats } = useCategoriesList({ isActive: true });
	const [categoryId, setCategoryId] = useState("");
	const { data: resolved } = useCategoryAttributes(categoryId || undefined);
	const setMut = useSetCategoryAttributes();
	const toast = useToast();

	// État local des assignations DIRECTES (héritées = lecture seule).
	const [direct, setDirect] = useState<
		Record<string, { checked: boolean; required: boolean }>
	>({});
	const inherited = useMemo(
		() => (resolved ?? []).filter((r) => r.inheritedFrom !== null),
		[resolved],
	);

	useEffect(() => {
		const next: Record<string, { checked: boolean; required: boolean }> = {};
		(resolved ?? [])
			.filter((r) => r.inheritedFrom === null)
			.forEach(
				(r) =>
					(next[r.attribute.id] = { checked: true, required: r.isRequired }),
			);
		setDirect(next);
	}, [resolved, categoryId]);

	const toggle = (id: string) =>
		setDirect((p) => ({
			...p,
			[id]: { checked: !p[id]?.checked, required: p[id]?.required ?? false },
		}));
	const toggleReq = (id: string) =>
		setDirect((p) => ({
			...p,
			[id]: { checked: p[id]?.checked ?? true, required: !p[id]?.required },
		}));

	const save = async () => {
		const assignments = Object.entries(direct)
			.filter(([, v]) => v.checked)
			.map(([attributeId, v], i) => ({
				attributeId,
				isRequired: v.required,
				sortOrder: i,
			}));
		try {
			await setMut.mutateAsync({ categoryId, assignments });
			toast.success("Attributs de la catégorie enregistrés.");
		} catch (e) {
			toast.error(extractErrorMessage(e, "Enregistrement impossible"));
		}
	};

	const inheritedIds = new Set(inherited.map((r) => r.attribute.id));

	return (
		<div className="card">
			<div className="card-h">
				<div>
					<h3>Attributs par catégorie</h3>
					<p className="sub muted">
						Choisissez la catégorie, cochez les caractéristiques qui
						s’appliquent.
					</p>
				</div>
				{categoryId && (
					<button
						className="btn btn-primary"
						onClick={() => void save()}
						disabled={setMut.isPending}
					>
						{setMut.isPending ? "Enregistrement…" : "Enregistrer"}
					</button>
				)}
			</div>
			<div className="card-b">
				<div style={{ maxWidth: 420, marginBottom: 14 }}>
					<CategoryPicker
						categories={cats ?? []}
						value={categoryId}
						onChange={setCategoryId}
					/>
				</div>
				{!categoryId && (
					<p className="muted" style={{ fontSize: 13 }}>
						Sélectionnez une catégorie.
					</p>
				)}
				{categoryId && (
					<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
						{attributes.map((a) => {
							const isInherited = inheritedIds.has(a.id);
							const d = direct[a.id];
							return (
								<div
									key={a.id}
									style={{
										display: "flex",
										alignItems: "center",
										gap: 12,
										padding: "8px 10px",
										border: "1px solid var(--a-border,#eee)",
										borderRadius: 6,
										opacity: isInherited ? 0.7 : 1,
									}}
								>
									<label
										style={{
											display: "flex",
											alignItems: "center",
											gap: 8,
											flex: 1,
											cursor: isInherited ? "default" : "pointer",
										}}
									>
										<input
											type="checkbox"
											disabled={isInherited}
											checked={isInherited || !!d?.checked}
											onChange={() => toggle(a.id)}
										/>
										<span style={{ fontWeight: 600 }}>{a.label}</span>
										<span
											className="tag neutral no-dot"
											style={{ fontSize: 11 }}
										>
											{TYPE_LABEL[a.type]}
										</span>
										{a.type === "select" && (
											<span className="muted" style={{ fontSize: 11 }}>
												{a.values.length} valeurs
											</span>
										)}
									</label>
									{isInherited ? (
										<span className="tag info no-dot" style={{ fontSize: 11 }}>
											hérité d’un parent
										</span>
									) : (
										d?.checked && (
											<label
												style={{
													display: "inline-flex",
													gap: 6,
													alignItems: "center",
													fontSize: 12,
												}}
											>
												<input
													type="checkbox"
													checked={!!d?.required}
													onChange={() => toggleReq(a.id)}
												/>
												Requis
											</label>
										)
									)}
								</div>
							);
						})}
						{attributes.length === 0 && (
							<p className="muted" style={{ fontSize: 13 }}>
								Créez d’abord des attributs ci-dessus.
							</p>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

export function AdminAttributesPage() {
	const { data: attributes, isLoading } = useAttributes();
	const deleteMut = useDeleteAttribute();
	const toast = useToast();
	const confirm = useConfirm();
	const [modal, setModal] = useState<"new" | AttributeDto | null>(null);

	const remove = async (a: AttributeDto) => {
		const ok = await confirm({
			title: `Supprimer « ${a.label} » ?`,
			message:
				"Refusé si l’attribut est rattaché à une catégorie ou utilisé par des produits.",
			confirmLabel: "Supprimer",
			tone: "danger",
		});
		if (!ok) return;
		try {
			await deleteMut.mutateAsync(a.id);
			toast.success("Attribut supprimé.");
		} catch (e) {
			toast.error(extractErrorMessage(e, "Suppression impossible"));
		}
	};

	const list = attributes ?? [];

	return (
		<>
			<PageHead
				title="Attributs"
				sub="Caractéristiques structurées du catalogue — définition, valeurs autorisées et rattachement aux catégories."
				actions={
					<button className="btn btn-primary" onClick={() => setModal("new")}>
						+ Nouvel attribut
					</button>
				}
			/>

			<div className="card" style={{ marginBottom: 16 }}>
				<div className="card-h">
					<h3>Attributs ({list.length})</h3>
				</div>
				<div className="card-b" style={{ padding: 0 }}>
					<table className="table" style={{ width: "100%" }}>
						<thead>
							<tr>
								<th>Attribut</th>
								<th>Type</th>
								<th>Valeurs autorisées</th>
								<th>Statut</th>
								<th style={{ width: 140 }}></th>
							</tr>
						</thead>
						<tbody>
							{isLoading && (
								<tr>
									<td
										colSpan={5}
										className="muted"
										style={{ padding: 20, textAlign: "center" }}
									>
										Chargement…
									</td>
								</tr>
							)}
							{!isLoading && list.length === 0 && (
								<tr>
									<td
										colSpan={5}
										className="muted"
										style={{ padding: 20, textAlign: "center" }}
									>
										Aucun attribut.
									</td>
								</tr>
							)}
							{list.map((a) => (
								<tr key={a.id}>
									<td style={{ fontWeight: 600 }}>
										{a.label}
										{a.unit ? ` (${a.unit})` : ""}
									</td>
									<td>{TYPE_LABEL[a.type]}</td>
									<td className="muted" style={{ fontSize: 12 }}>
										{a.type === "select"
											? a.values.map((v) => v.value).join(", ") || "—"
											: "—"}
									</td>
									<td>
										<span
											className={`tag ${a.isActive ? "success" : "neutral"} no-dot`}
										>
											{a.isActive ? "Actif" : "Inactif"}
										</span>
									</td>
									<td>
										<div
											style={{
												display: "flex",
												gap: 6,
												justifyContent: "flex-end",
											}}
										>
											<button
												className="btn btn-ghost btn-sm"
												onClick={() => setModal(a)}
											>
												Éditer
											</button>
											<button
												className="btn btn-ghost btn-sm"
												onClick={() => void remove(a)}
											>
												Suppr.
											</button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>

			<CategoryAssignment attributes={list} />

			{modal !== null && (
				<AttributeModal target={modal} onClose={() => setModal(null)} />
			)}
		</>
	);
}
